import {
  ApiError,
  ApiNetworkError,
  type SetupCalendarResponse,
  type SetupDriveResponse,
  type SetupEmailResponse,
  type SetupPmsDescriptor,
  type SetupPmsResponse,
  type SetupStateResponse,
  pmSetupCalendar,
  pmSetupDisconnectCalendar,
  pmSetupDisconnectDrive,
  pmSetupDisconnectEmail,
  pmSetupDisconnectPms,
  pmSetupDrive,
  pmSetupEmail,
  pmSetupPms,
  pmSetupStart,
  pmSetupState,
} from '@cortex/web-api/clients/web/api';
import { Wordmark } from '@cortex/web-api/ui/web/Wordmark';
import {
  type FormEvent,
  type ReactElement,
  cloneElement,
  useEffect,
  useId,
  useMemo,
  useState,
} from 'react';

/**
 * v1.2 PM-onboarding (PR2) — /setup wizard surface.
 *
 * Lifecycle:
 *   1. PM lands on `<slug>.acelcortex.ai/setup#token=<plaintext>` (link from
 *      operator's email, generated by /pm/setup/invite at PR1).
 *   2. On mount, extract token from URL fragment, POST /pm/setup/start →
 *      consumes invite atomically + sets cortex_setup_session cookie.
 *      `history.replaceState` immediately clears the fragment so the token
 *      doesn't sit in browser history (defense per PR1's adversarial review).
 *   3. GET /pm/setup/state to figure out which cards still need attention.
 *   4. Render the active card (PR2 = Buildium only; PR3 wires email +
 *      calendar; PR5 wires phone OTP).
 *
 * PR2 ships only the Buildium card. The other cards render disabled with
 * a "lands in PR-N" tooltip — same shape as the admin TenantDetailScreen.
 */

type LifecyclePhase =
  | { phase: 'loading' }
  | { phase: 'ready'; state: SetupStateResponse }
  | { phase: 'invalid_token' }
  | { phase: 'no_token' }
  | { phase: 'fatal'; message: string };

export function SetupScreen() {
  const [lifecycle, setLifecycle] = useState<LifecyclePhase>({ phase: 'loading' });

  useEffect(() => {
    void bootstrap().then(setLifecycle);
  }, []);

  if (lifecycle.phase === 'loading') {
    return <FullScreenStatus title="Loading your setup…" />;
  }
  if (lifecycle.phase === 'no_token') {
    return (
      <FullScreenStatus
        title="No setup link found"
        body="Open the link your property manager emailed you. It looks like https://your-company.acelcortex.com/setup#token=…"
      />
    );
  }
  if (lifecycle.phase === 'invalid_token') {
    return (
      <FullScreenStatus
        title="That link is no longer valid"
        body="It may have expired or already been used. Ask your property manager to send a fresh invite."
      />
    );
  }
  if (lifecycle.phase === 'fatal') {
    return <FullScreenStatus title="Something went wrong" body={lifecycle.message} />;
  }

  return (
    <SetupBody
      state={lifecycle.state}
      onStateUpdated={(state) => setLifecycle({ phase: 'ready', state })}
    />
  );
}

async function bootstrap(): Promise<LifecyclePhase> {
  const token = readTokenFromHash();
  if (!token) {
    return { phase: 'no_token' };
  }

  try {
    await pmSetupStart(token);
  } catch (err) {
    // Leave the fragment in place on failure so a refresh can retry the same
    // link — clearing it on network error would force the PM to ask the
    // operator for a new invite when their connection blip is the issue.
    if (err instanceof ApiError && err.status === 410) {
      window.history.replaceState(null, '', '/setup');
      return { phase: 'invalid_token' };
    }
    if (err instanceof ApiError && err.status === 400) {
      window.history.replaceState(null, '', '/setup');
      return { phase: 'invalid_token' };
    }
    return {
      phase: 'fatal',
      message: errorMessage(err) ?? "Couldn't start your setup. Try the link again.",
    };
  }
  // Clear the fragment AFTER successful consumption so the token doesn't
  // persist in browser history (defense per PR1's adversarial review).
  window.history.replaceState(null, '', '/setup');

  try {
    const state = await pmSetupState();
    return { phase: 'ready', state };
  } catch (err) {
    return {
      phase: 'fatal',
      message: errorMessage(err) ?? "Couldn't load your setup state.",
    };
  }
}

function readTokenFromHash(): string | null {
  const hash = window.location.hash.replace(/^#/, '');
  if (!hash) return null;
  const params = new URLSearchParams(hash);
  return params.get('token');
}

function errorMessage(err: unknown): string | null {
  if (err instanceof ApiNetworkError) return "Couldn't reach our servers. Check your connection.";
  if (err instanceof ApiError) return `Server returned ${err.status}.`;
  return null;
}

// ---------------------------------------------------------------------------

interface BodyProps {
  state: SetupStateResponse;
  onStateUpdated: (state: SetupStateResponse) => void;
}

function SetupBody({ state, onStateUpdated }: BodyProps) {
  return (
    <div className="app">
      <header className="app-header">
        <Wordmark />
      </header>
      <main
        className="app-main"
        style={{ overflow: 'auto', padding: 'var(--space-8) var(--space-6)' }}
      >
        <div
          style={{
            maxWidth: 640,
            margin: '0 auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-6)',
          }}
        >
          <Heading state={state} />
          <PmsCard state={state} onStateUpdated={onStateUpdated} />
          <EmailCard state={state} onStateUpdated={onStateUpdated} />
          <CalendarCard state={state} onStateUpdated={onStateUpdated} />
          <DriveCard state={state} onStateUpdated={onStateUpdated} />
          <ComingSoonCard
            title="Phone login"
            connected={state.cards.phone.connected}
            note="lands when PM dashboard ships (PR5)"
          />
        </div>
      </main>
      <footer className="app-footer">acel·cortex setup</footer>
    </div>
  );
}

function Heading({ state }: { state: SetupStateResponse }) {
  const total = 5;
  const pmsConnected =
    state.pms?.connection.connected ?? state.cards.pms?.connected ?? state.cards.buildium.connected;
  const done =
    Number(pmsConnected) +
    Number(state.cards.email.connected) +
    Number(state.cards.calendar.connected) +
    Number(state.cards.drive.connected) +
    Number(state.cards.phone.connected);
  return (
    <div>
      <h1 className="h2" style={{ color: 'var(--ink)' }}>
        connect cortex to your tools
      </h1>
      <p
        style={{
          color: 'var(--muted)',
          fontFamily: 'var(--font-mono)',
          fontSize: 'var(--fs-mono-sm)',
          letterSpacing: 'var(--tracking-mono)',
          textTransform: 'uppercase',
          marginTop: 'var(--space-2)',
        }}
      >
        {done} of {total} steps complete · signed in as {state.pmPhone}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------

interface PmsCardProps {
  state: SetupStateResponse;
  onStateUpdated: (state: SetupStateResponse) => void;
}

function PmsCard({ state, onStateUpdated }: PmsCardProps) {
  const descriptor = useMemo(
    () => state.pms ?? legacyBuildiumDescriptor(state.cards.buildium.connected),
    [state.pms, state.cards.buildium.connected],
  );
  const isConnected =
    descriptor.connection.connected ?? state.cards.pms?.connected ?? state.cards.buildium.connected;
  const [showChangeForm, setShowChangeForm] = useState(false);
  const [values, setValues] = useState<Record<string, string>>(() =>
    initialPmsFieldValues(descriptor),
  );
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<SetupPmsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [disconnectBusy, setDisconnectBusy] = useState(false);
  const [disconnectError, setDisconnectError] = useState<string | null>(null);

  useEffect(() => {
    setValues(initialPmsFieldValues(descriptor));
    setResult(null);
    setError(null);
  }, [descriptor]);

  function resetFormState() {
    setShowChangeForm(false);
    setValues(initialPmsFieldValues(descriptor));
    setResult(null);
    setError(null);
  }

  async function handleSubmit(ev: FormEvent) {
    ev.preventDefault();
    setError(null);
    const missing = descriptor.fields.find((field) => field.required && !values[field.key]?.trim());
    if (missing) {
      setError(`${missing.label} is required.`);
      return;
    }
    setBusy(true);
    try {
      const credentials = Object.fromEntries(
        descriptor.fields.map((field) => [field.key, values[field.key]?.trim() ?? '']),
      );
      const res = await pmSetupPms({
        provider: descriptor.provider,
        credentials,
      });
      setResult(res);
      if (res.status === 'connected') {
        // Refresh /pm/setup/state so the heading + card transitions to "done"
        // or, in change-mode, back to a settled "connected" with new creds.
        const fresh = await pmSetupState();
        onStateUpdated(fresh);
        resetFormState();
      }
    } catch (err) {
      setError(errorMessage(err) ?? "Couldn't reach the server.");
    } finally {
      setBusy(false);
    }
  }

  async function handleDisconnect() {
    setDisconnectError(null);
    setDisconnectBusy(true);
    try {
      await pmSetupDisconnectPms();
      const fresh = await pmSetupState();
      onStateUpdated(fresh);
      resetFormState();
    } catch (err) {
      setDisconnectError(errorMessage(err) ?? "Couldn't disconnect.");
    } finally {
      setDisconnectBusy(false);
    }
  }

  if (isConnected && !showChangeForm) {
    return (
      <div className="card" style={{ padding: 'var(--space-6)' }}>
        <CardHeader title={descriptor.label} connected={true} />
        <p style={{ color: 'var(--muted)', marginTop: 'var(--space-3)' }}>
          Connected. {descriptor.description}
        </p>
        <PmsConnectionDetails descriptor={descriptor} />
        <CardActions
          onChange={() => setShowChangeForm(true)}
          onDisconnect={handleDisconnect}
          disconnectBusy={disconnectBusy}
          disconnectError={disconnectError}
        />
      </div>
    );
  }

  if (descriptor.setupMode === 'operations_only') {
    return (
      <div className="card" style={{ padding: 'var(--space-6)' }}>
        <CardHeader title={descriptor.label} connected={isConnected} />
        <p style={{ color: 'var(--ink-soft)', marginTop: 'var(--space-3)' }}>
          {descriptor.description}
        </p>
        {descriptor.connection.safeError && (
          <p className="field-error" style={{ marginTop: 'var(--space-3)' }}>
            {descriptor.connection.safeError}
          </p>
        )}
        <p
          style={{
            color: 'var(--muted)',
            marginTop: 'var(--space-3)',
            fontFamily: 'var(--font-mono)',
            fontSize: 'var(--fs-mono-sm)',
          }}
        >
          Contact Cortex support to configure or change this connection.
        </p>
        <PmsConnectionDetails descriptor={descriptor} />
      </div>
    );
  }

  return (
    <div className="card" style={{ padding: 'var(--space-6)' }}>
      <CardHeader title={descriptor.label} connected={isConnected} />
      {isConnected && (
        <ChangeFormHint
          message={`Enter different ${descriptor.label} credentials below to switch.`}
          onCancel={resetFormState}
        />
      )}
      <p style={{ color: 'var(--ink-soft)', marginTop: 'var(--space-3)' }}>
        {descriptor.description}
      </p>
      {descriptor.connection.safeError && (
        <p className="field-error" style={{ marginTop: 'var(--space-3)' }}>
          {descriptor.connection.safeError}
        </p>
      )}
      <form
        onSubmit={handleSubmit}
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-4)',
          marginTop: 'var(--space-4)',
        }}
      >
        {descriptor.fields.map((field) => (
          <Field key={field.key} label={field.label} hint={field.hint}>
            {field.inputType === 'select' ? (
              <select
                className="field-input mono"
                value={values[field.key] ?? ''}
                onChange={(e) =>
                  setValues((current) => ({ ...current, [field.key]: e.target.value }))
                }
                disabled={busy}
              >
                {(field.options ?? []).map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            ) : (
              <input
                className="field-input mono"
                type={field.secret || field.inputType === 'password' ? 'password' : 'text'}
                value={values[field.key] ?? ''}
                onChange={(e) =>
                  setValues((current) => ({ ...current, [field.key]: e.target.value }))
                }
                disabled={busy}
                autoComplete={field.secret ? 'new-password' : 'off'}
                placeholder={field.placeholder}
              />
            )}
          </Field>
        ))}

        {error && (
          <p className="field-error" style={{ marginTop: 'var(--space-2)' }}>
            {error}
          </p>
        )}

        {result && result.status === 'connected' && (
          <div
            style={{
              padding: 'var(--space-3)',
              background: 'var(--paper-2)',
              borderRadius: 6,
              fontFamily: 'var(--font-mono)',
              fontSize: 'var(--fs-mono-sm)',
              color: 'var(--green)',
            }}
          >
            ✓ Connected — {formatPmsCounts(result.counts)}
          </div>
        )}
        {result && result.status === 'verification_failed' && (
          <div
            style={{
              padding: 'var(--space-3)',
              background: 'var(--paper-2)',
              borderRadius: 6,
              fontFamily: 'var(--font-mono)',
              fontSize: 'var(--fs-mono-sm)',
              color: '#c84a3b',
            }}
          >
            {messageForPmsErrorClass(result.errorClass, result.message)}
          </div>
        )}

        <button type="submit" className="btn-primary" disabled={busy}>
          {busy ? 'verifying…' : `connect to ${descriptor.label.toLowerCase()}`}
        </button>
      </form>
    </div>
  );
}

function messageForPmsErrorClass(
  c: Extract<SetupPmsResponse, { status: 'verification_failed' }>['errorClass'],
  safeMessage?: string,
): string {
  if (safeMessage) return `✗ ${safeMessage}`;
  switch (c) {
    case 'bad_credentials':
      return '✗ The PMS rejected these credentials. Double-check the key and secret.';
    case 'scope_insufficient':
      return '✗ The key works but its role lacks one or more required permissions. Update the least-privilege role and verify again.';
    case 'base_url_mismatch':
      return '✗ The account or API origin does not match these credentials.';
    case 'rate_limited':
      return '✗ The PMS rate-limited verification. Wait for the provider retry window and try again.';
    case 'unreachable':
      return "✗ Couldn't reach the PMS. Check the account value or try again in a moment.";
    case 'provider_mismatch':
      return '✗ These credentials do not match the PMS assigned to this tenant.';
    case 'contract_unverified':
      return '✗ The connection works, but the required API contract has not passed sandbox verification.';
    case 'operations_only':
      return '✗ This PMS connection is provisioned by Cortex operations.';
    case 'connection_changed':
      return '✗ The PMS connection changed while verification was running. Review its current state before trying again.';
    case 'persistence_failed':
      return "✗ Cortex couldn't save the verified connection. Try again or contact support.";
  }
}

function legacyBuildiumDescriptor(connected: boolean): SetupPmsDescriptor {
  return {
    provider: 'buildium',
    setupMode: 'self_service',
    label: 'Buildium',
    description:
      'Paste an API key from the Buildium developer portal. Cortex verifies property, vendor, and lease access before saving it.',
    fields: [
      { key: 'clientId', label: 'client id', inputType: 'text', required: true },
      {
        key: 'clientSecret',
        label: 'client secret',
        inputType: 'password',
        required: true,
        secret: true,
      },
      {
        key: 'baseUrl',
        label: 'base url',
        hint: 'sandbox or production',
        inputType: 'select',
        required: true,
        defaultValue: 'https://api.buildium.com/v1',
        options: [
          { label: 'Production (api.buildium.com)', value: 'https://api.buildium.com/v1' },
          {
            label: 'Sandbox (apisandbox.buildium.com)',
            value: 'https://apisandbox.buildium.com/v1',
          },
        ],
      },
    ],
    connection: {
      status: connected ? 'active' : 'missing',
      connected,
      verifiedCapabilities: [],
      enabledCapabilities: [],
      grantedCapabilities: [],
      safeSummary: {},
      lastVerifiedAt: null,
      lastSyncAt: null,
      safeError: null,
    },
  };
}

function initialPmsFieldValues(descriptor: SetupPmsDescriptor): Record<string, string> {
  return Object.fromEntries(
    descriptor.fields.map((field) => [
      field.key,
      field.defaultValue ?? field.options?.[0]?.value ?? '',
    ]),
  );
}

function formatPmsCounts(counts: Record<string, number>): string {
  const entries = Object.entries(counts).filter(([, count]) => Number.isFinite(count));
  if (!entries.length) return 'credential and permission checks passed.';
  return `${entries.map(([name, count]) => `${count} ${name}`).join(', ')}.`;
}

function PmsConnectionDetails({ descriptor }: { descriptor: SetupPmsDescriptor }) {
  const { connection } = descriptor;
  const runtimeConfiguration = descriptor.runtimeConfiguration ?? {
    fields: [],
    configuredKeys: [],
  };
  return (
    <div
      style={{
        marginTop: 'var(--space-3)',
        fontFamily: 'var(--font-mono)',
        fontSize: 'var(--fs-mono-sm)',
        color: 'var(--muted)',
      }}
    >
      <div>
        verified capabilities:{' '}
        {connection.verifiedCapabilities.length
          ? connection.verifiedCapabilities.join(', ')
          : 'none verified'}
      </div>
      <div>
        enabled capabilities:{' '}
        {connection.enabledCapabilities.length
          ? connection.enabledCapabilities.join(', ')
          : 'none enabled'}
      </div>
      <div>last verified: {connection.lastVerifiedAt ?? 'never'}</div>
      <div>last sync: {connection.lastSyncAt ?? 'never'}</div>
      {runtimeConfiguration.fields.length ? (
        <div>
          runtime mappings:{' '}
          {runtimeConfiguration.configuredKeys.length
            ? runtimeConfiguration.configuredKeys.join(', ')
            : 'not configured (Cortex operations setup required)'}
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------

function EmailCard({
  state,
  onStateUpdated,
}: {
  state: SetupStateResponse;
  onStateUpdated: (state: SetupStateResponse) => void;
}) {
  const isConnected = state.cards.email.connected;
  const [showChangeForm, setShowChangeForm] = useState(false);
  const [forwardTo, setForwardTo] = useState('');
  const [accountingInvoiceAddress, setAccountingInvoiceAddress] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<SetupEmailResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [disconnectBusy, setDisconnectBusy] = useState(false);
  const [disconnectError, setDisconnectError] = useState<string | null>(null);

  function resetFormState() {
    setShowChangeForm(false);
    setForwardTo('');
    setAccountingInvoiceAddress('');
    setResult(null);
    setError(null);
  }

  async function handleSubmit(ev: FormEvent) {
    ev.preventDefault();
    setError(null);
    if (!forwardTo.trim() || !accountingInvoiceAddress.trim()) {
      setError('Enter both your reply inbox and company invoice address.');
      return;
    }
    setBusy(true);
    try {
      const res = await pmSetupEmail({
        forwardTo: forwardTo.trim(),
        accountingInvoiceAddress: accountingInvoiceAddress.trim(),
      });
      setResult(res);
      if (res.status === 'connected') {
        const fresh = await pmSetupState();
        onStateUpdated(fresh);
        resetFormState();
      }
    } catch (err) {
      setError(errorMessage(err) ?? "Couldn't reach the server.");
    } finally {
      setBusy(false);
    }
  }

  async function handleDisconnect() {
    setDisconnectError(null);
    setDisconnectBusy(true);
    try {
      await pmSetupDisconnectEmail();
      const fresh = await pmSetupState();
      onStateUpdated(fresh);
      resetFormState();
    } catch (err) {
      setDisconnectError(errorMessage(err) ?? "Couldn't disconnect.");
    } finally {
      setDisconnectBusy(false);
    }
  }

  if (isConnected && !showChangeForm) {
    return (
      <div className="card" style={{ padding: 'var(--space-6)' }}>
        <CardHeader title="Email forwarding" connected={true} />
        <p style={{ color: 'var(--muted)', marginTop: 'var(--space-3)' }}>
          Connected. Reply forwarding and your company invoice mailbox are configured.
        </p>
        <CardActions
          onChange={() => setShowChangeForm(true)}
          onDisconnect={handleDisconnect}
          disconnectBusy={disconnectBusy}
          disconnectError={disconnectError}
        />
      </div>
    );
  }

  return (
    <div className="card" style={{ padding: 'var(--space-6)' }}>
      <CardHeader title="Email forwarding" connected={isConnected} />
      {isConnected && (
        <ChangeFormHint
          message="Enter a different forwarding address to switch."
          onCancel={resetFormState}
        />
      )}
      <p style={{ color: 'var(--ink-soft)', marginTop: 'var(--space-3)' }}>
        Cortex sends vendor + tenant emails from a Cortex-branded address. Replies go to the inbox
        you choose here. Separately, enter the company AP mailbox vendors use for invoice PDFs so it
        can appear on your Accounting dashboard.
      </p>
      <p
        style={{
          color: 'var(--muted)',
          marginTop: 'var(--space-2)',
          fontSize: 'var(--fs-mono-sm)',
          fontFamily: 'var(--font-mono)',
        }}
      >
        Cloudflare will email this address a confirmation link the first time — click it before
        continuing, or the rule won't activate.
      </p>
      <form
        onSubmit={handleSubmit}
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-4)',
          marginTop: 'var(--space-4)',
        }}
      >
        <Field label="reply forwarding inbox">
          <input
            className="field-input mono"
            type="email"
            value={forwardTo}
            onChange={(e) => setForwardTo(e.target.value)}
            disabled={busy}
            placeholder="you@example.com"
          />
        </Field>
        <Field label="company invoice address" hint="vendors send invoice PDFs here">
          <input
            className="field-input mono"
            type="email"
            value={accountingInvoiceAddress}
            onChange={(e) => setAccountingInvoiceAddress(e.target.value)}
            disabled={busy}
            placeholder="invoices@your-company.com"
          />
        </Field>

        {error && (
          <p className="field-error" style={{ marginTop: 'var(--space-2)' }}>
            {error}
          </p>
        )}
        {result && result.status === 'connected' && (
          <div
            style={{
              padding: 'var(--space-3)',
              background: 'var(--paper-2)',
              borderRadius: 6,
              fontFamily: 'var(--font-mono)',
              fontSize: 'var(--fs-mono-sm)',
              color: 'var(--green)',
            }}
          >
            ✓ Connected — emails to {result.address} forward to {result.forwardTo}.
            <br />✓ Accounting will show {result.accountingInvoiceAddress} as your vendor invoice
            address.
          </div>
        )}
        {result && result.status === 'cloudflare_failed' && (
          <div
            style={{
              padding: 'var(--space-3)',
              background: 'var(--paper-2)',
              borderRadius: 6,
              fontFamily: 'var(--font-mono)',
              fontSize: 'var(--fs-mono-sm)',
              color: '#c84a3b',
            }}
          >
            {messageForEmailErrorClass(result.errorClass)}
          </div>
        )}

        <button type="submit" className="btn-primary" disabled={busy}>
          {busy ? 'connecting…' : 'connect email'}
        </button>
      </form>
    </div>
  );
}

function messageForEmailErrorClass(
  c: 'destination_not_verified' | 'invalid_destination' | 'cloudflare_unreachable',
): string {
  switch (c) {
    case 'destination_not_verified':
      return "✗ That email isn't verified with Cloudflare yet. Check your inbox for the confirmation email Cloudflare sent, click the link, then try again.";
    case 'invalid_destination':
      return '✗ Cloudflare rejected that address. Double-check for typos.';
    case 'cloudflare_unreachable':
      return "✗ Couldn't reach Cloudflare. Try again in a moment.";
  }
}

// ---------------------------------------------------------------------------

const SA_EMAIL = 'pm-agent@jarvis-assistant-485904.iam.gserviceaccount.com';

function CalendarCard({
  state,
  onStateUpdated,
}: {
  state: SetupStateResponse;
  onStateUpdated: (state: SetupStateResponse) => void;
}) {
  const isConnected = state.cards.calendar.connected;
  const [showChangeForm, setShowChangeForm] = useState(false);
  const [calendarId, setCalendarId] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<SetupCalendarResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [disconnectBusy, setDisconnectBusy] = useState(false);
  const [disconnectError, setDisconnectError] = useState<string | null>(null);

  function resetFormState() {
    setShowChangeForm(false);
    setCalendarId('');
    setResult(null);
    setError(null);
  }

  async function handleSubmit(ev: FormEvent) {
    ev.preventDefault();
    setError(null);
    if (!calendarId.trim()) {
      setError('Paste your Calendar ID.');
      return;
    }
    setBusy(true);
    try {
      const res = await pmSetupCalendar({ calendarId: calendarId.trim() });
      setResult(res);
      if (res.status === 'connected') {
        const fresh = await pmSetupState();
        onStateUpdated(fresh);
        resetFormState();
      }
    } catch (err) {
      setError(errorMessage(err) ?? "Couldn't reach the server.");
    } finally {
      setBusy(false);
    }
  }

  async function handleDisconnect() {
    setDisconnectError(null);
    setDisconnectBusy(true);
    try {
      await pmSetupDisconnectCalendar();
      const fresh = await pmSetupState();
      onStateUpdated(fresh);
      resetFormState();
    } catch (err) {
      setDisconnectError(errorMessage(err) ?? "Couldn't disconnect.");
    } finally {
      setDisconnectBusy(false);
    }
  }

  if (isConnected && !showChangeForm) {
    return (
      <div className="card" style={{ padding: 'var(--space-6)' }}>
        <CardHeader title="Calendar" connected={true} />
        <p style={{ color: 'var(--muted)', marginTop: 'var(--space-3)' }}>
          Connected. Cortex can add vendor visits to your calendar.
        </p>
        <CardActions
          onChange={() => setShowChangeForm(true)}
          onDisconnect={handleDisconnect}
          disconnectBusy={disconnectBusy}
          disconnectError={disconnectError}
        />
      </div>
    );
  }

  return (
    <div className="card" style={{ padding: 'var(--space-6)' }}>
      <CardHeader title="Calendar" connected={isConnected} />
      {isConnected && (
        <ChangeFormHint
          message="Paste a different Calendar ID below to switch."
          onCancel={resetFormState}
        />
      )}
      <p style={{ color: 'var(--ink-soft)', marginTop: 'var(--space-3)' }}>
        Cortex adds vendor visits to your existing Google Calendar. You stay the owner of every
        event; Cortex just creates them.
      </p>
      <ol
        style={{
          color: 'var(--ink-soft)',
          marginTop: 'var(--space-3)',
          marginLeft: 'var(--space-5)',
          lineHeight: '1.7',
        }}
      >
        <li>
          In Google Calendar, pick the calendar (or create a new one called "Vendor Visits").
          Settings → "Share with specific people" → add this email with permission{' '}
          <strong>"Make changes to events"</strong>:
          <pre
            style={{
              padding: 'var(--space-2)',
              background: 'var(--paper-2)',
              borderRadius: 6,
              fontFamily: 'var(--font-mono)',
              fontSize: 'var(--fs-mono-sm)',
              marginTop: 'var(--space-2)',
              overflow: 'auto',
            }}
          >
            {SA_EMAIL}
          </pre>
        </li>
        <li>
          Same Settings page → "Integrate calendar" → copy the <strong>Calendar ID</strong> → paste
          below.
        </li>
      </ol>
      <form
        onSubmit={handleSubmit}
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-4)',
          marginTop: 'var(--space-4)',
        }}
      >
        <Field label="calendar id">
          <input
            className="field-input mono"
            value={calendarId}
            onChange={(e) => setCalendarId(e.target.value)}
            disabled={busy}
            placeholder="abc123@group.calendar.google.com"
          />
        </Field>

        {error && (
          <p className="field-error" style={{ marginTop: 'var(--space-2)' }}>
            {error}
          </p>
        )}
        {result && result.status === 'connected' && (
          <div
            style={{
              padding: 'var(--space-3)',
              background: 'var(--paper-2)',
              borderRadius: 6,
              fontFamily: 'var(--font-mono)',
              fontSize: 'var(--fs-mono-sm)',
              color: 'var(--green)',
            }}
          >
            ✓ Connected to "{result.summary}" (write access verified).
          </div>
        )}
        {result && result.status === 'verification_failed' && (
          <div
            style={{
              padding: 'var(--space-3)',
              background: 'var(--paper-2)',
              borderRadius: 6,
              fontFamily: 'var(--font-mono)',
              fontSize: 'var(--fs-mono-sm)',
              color: '#c84a3b',
            }}
          >
            {messageForCalendarErrorClass(result.errorClass)}
          </div>
        )}

        <button type="submit" className="btn-primary" disabled={busy}>
          {busy ? 'verifying…' : 'verify and connect'}
        </button>
      </form>
    </div>
  );
}

function messageForCalendarErrorClass(
  c: 'calendar_not_found' | 'not_shared' | 'read_only_share' | 'unreachable',
): string {
  switch (c) {
    case 'calendar_not_found':
      return "✗ Couldn't find that calendar. Double-check the ID — it should look like an email address ending in @group.calendar.google.com.";
    case 'not_shared':
      return `✗ Looks like you haven't shared the calendar with us yet. Go back to step 1 and add ${SA_EMAIL} to the calendar's sharing settings.`;
    case 'read_only_share':
      return '✗ The calendar is shared as "See all event details" (read-only). Change the permission to "Make changes to events" so Cortex can add vendor visits.';
    case 'unreachable':
      return "✗ Couldn't reach Google Calendar. Check your connection or try again in a moment.";
  }
}

// ---------------------------------------------------------------------------

function DriveCard({
  state,
  onStateUpdated,
}: {
  state: SetupStateResponse;
  onStateUpdated: (state: SetupStateResponse) => void;
}) {
  const isConnected = state.cards.drive.connected;
  const [showChangeForm, setShowChangeForm] = useState(false);
  const [folderId, setFolderId] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<SetupDriveResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [disconnectBusy, setDisconnectBusy] = useState(false);
  const [disconnectError, setDisconnectError] = useState<string | null>(null);

  function resetFormState() {
    setShowChangeForm(false);
    setFolderId('');
    setResult(null);
    setError(null);
  }

  async function handleSubmit(ev: FormEvent) {
    ev.preventDefault();
    setError(null);
    if (!folderId.trim()) {
      setError('Paste your Drive folder ID.');
      return;
    }
    setBusy(true);
    try {
      const res = await pmSetupDrive({ folderId: folderId.trim() });
      setResult(res);
      if (res.status === 'connected') {
        const fresh = await pmSetupState();
        onStateUpdated(fresh);
        resetFormState();
      }
    } catch (err) {
      setError(errorMessage(err) ?? "Couldn't reach the server.");
    } finally {
      setBusy(false);
    }
  }

  async function handleDisconnect() {
    setDisconnectError(null);
    setDisconnectBusy(true);
    try {
      await pmSetupDisconnectDrive();
      const fresh = await pmSetupState();
      onStateUpdated(fresh);
      resetFormState();
    } catch (err) {
      setDisconnectError(errorMessage(err) ?? "Couldn't disconnect.");
    } finally {
      setDisconnectBusy(false);
    }
  }

  if (isConnected && !showChangeForm) {
    return (
      <div className="card" style={{ padding: 'var(--space-6)' }}>
        <CardHeader title="Drive" connected={true} />
        <p style={{ color: 'var(--muted)', marginTop: 'var(--space-3)' }}>
          Connected. Cortex can drop filled N4 PDFs into your Drive folder when you ask it to
          prepare N4s.
        </p>
        <CardActions
          onChange={() => setShowChangeForm(true)}
          onDisconnect={handleDisconnect}
          disconnectBusy={disconnectBusy}
          disconnectError={disconnectError}
        />
      </div>
    );
  }

  return (
    <div className="card" style={{ padding: 'var(--space-6)' }}>
      <CardHeader title="Drive" connected={isConnected} />
      {isConnected && (
        <ChangeFormHint
          message="Paste a different folder ID below to switch."
          onCancel={resetFormState}
        />
      )}
      <p style={{ color: 'var(--ink-soft)', marginTop: 'var(--space-3)' }}>
        Cortex uploads filled N4 PDFs into a folder in your Google Drive when you ask it to "prepare
        N4s". The files land at <span className="mono">/N4s/&lt;YYYY-MM&gt;/</span> so each monthly
        batch stays separate.
      </p>
      <ol
        style={{
          color: 'var(--ink-soft)',
          marginTop: 'var(--space-3)',
          marginLeft: 'var(--space-5)',
          lineHeight: '1.7',
        }}
      >
        <li>
          In Google Drive, create a folder (or pick an existing one, e.g. "Acel Cortex").
          Right-click → Share → add this email with permission <strong>"Editor"</strong>:
          <pre
            style={{
              padding: 'var(--space-2)',
              background: 'var(--paper-2)',
              borderRadius: 6,
              fontFamily: 'var(--font-mono)',
              fontSize: 'var(--fs-mono-sm)',
              marginTop: 'var(--space-2)',
              overflow: 'auto',
            }}
          >
            {SA_EMAIL}
          </pre>
        </li>
        <li>
          Open the folder. The URL looks like{' '}
          <span className="mono">drive.google.com/drive/folders/&lt;ID&gt;</span> — copy the ID (the
          part after <span className="mono">/folders/</span>) and paste below.
        </li>
      </ol>
      <form
        onSubmit={handleSubmit}
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-4)',
          marginTop: 'var(--space-4)',
        }}
      >
        <Field label="folder id">
          <input
            className="field-input mono"
            value={folderId}
            onChange={(e) => setFolderId(e.target.value)}
            disabled={busy}
            placeholder="1A2bCdEfGhIjKlMnOpQrStUvWxYz"
          />
        </Field>

        {error && (
          <p className="field-error" style={{ marginTop: 'var(--space-2)' }}>
            {error}
          </p>
        )}
        {result && result.status === 'connected' && (
          <div
            style={{
              padding: 'var(--space-3)',
              background: 'var(--paper-2)',
              borderRadius: 6,
              fontFamily: 'var(--font-mono)',
              fontSize: 'var(--fs-mono-sm)',
              color: 'var(--green)',
            }}
          >
            ✓ Connected to "{result.folderName}" (write access verified).
          </div>
        )}
        {result && result.status === 'verification_failed' && (
          <div
            style={{
              padding: 'var(--space-3)',
              background: 'var(--paper-2)',
              borderRadius: 6,
              fontFamily: 'var(--font-mono)',
              fontSize: 'var(--fs-mono-sm)',
              color: '#c84a3b',
            }}
          >
            {messageForDriveErrorClass(result.errorClass)}
          </div>
        )}

        <button type="submit" className="btn-primary" disabled={busy}>
          {busy ? 'verifying…' : 'verify and connect'}
        </button>
      </form>
    </div>
  );
}

function messageForDriveErrorClass(
  c: 'folder_not_found' | 'not_shared' | 'not_a_folder' | 'read_only_share' | 'unreachable',
): string {
  switch (c) {
    case 'folder_not_found':
      return "✗ Couldn't find that folder. Double-check the ID — it's the part of the Drive URL after /folders/.";
    case 'not_shared':
      return `✗ Looks like you haven't shared the folder with us yet. Go back to step 1 and add ${SA_EMAIL} as Editor on the folder.`;
    case 'not_a_folder':
      return '✗ That ID points to a file, not a folder. Open the folder in Drive and copy the ID from the URL (the part after /folders/).';
    case 'read_only_share':
      return '✗ The folder is shared as Viewer (read-only). Change the permission to "Editor" so Cortex can upload N4 PDFs.';
    case 'unreachable':
      return "✗ Couldn't reach Google Drive. Check your connection or try again in a moment.";
  }
}

// ---------------------------------------------------------------------------

function ComingSoonCard({
  title,
  connected,
  note,
}: {
  title: string;
  connected: boolean;
  note: string;
}) {
  return (
    <div className="card" style={{ padding: 'var(--space-6)', opacity: 0.7 }}>
      <CardHeader title={title} connected={connected} />
      <p
        style={{
          color: 'var(--muted)',
          marginTop: 'var(--space-3)',
          fontFamily: 'var(--font-mono)',
          fontSize: 'var(--fs-mono-sm)',
        }}
      >
        {note}
      </p>
    </div>
  );
}

function CardActions({
  onChange,
  onDisconnect,
  disconnectBusy,
  disconnectError,
}: {
  onChange: () => void;
  onDisconnect: () => void;
  disconnectBusy: boolean;
  disconnectError: string | null;
}) {
  // Two-button affordance on connected cards: "change" reveals the form
  // (old creds stay live until new submit succeeds), "disconnect" wipes
  // immediately. The link-style disconnect keeps the destructive action
  // visually subordinate to change, per the design system's "quiet
  // confidence" voice.
  return (
    <div style={{ marginTop: 'var(--space-4)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)' }}>
        <button
          type="button"
          className="btn-secondary"
          onClick={onChange}
          disabled={disconnectBusy}
        >
          change
        </button>
        <button
          type="button"
          onClick={onDisconnect}
          disabled={disconnectBusy}
          style={{
            background: 'transparent',
            border: 'none',
            padding: 0,
            color: 'var(--muted)',
            fontFamily: 'var(--font-mono)',
            fontSize: 'var(--fs-mono-sm)',
            letterSpacing: 'var(--tracking-mono)',
            textTransform: 'uppercase',
            textDecoration: 'underline',
            cursor: disconnectBusy ? 'not-allowed' : 'pointer',
          }}
        >
          {disconnectBusy ? 'disconnecting…' : 'disconnect'}
        </button>
      </div>
      {disconnectError && (
        <p className="field-error" style={{ marginTop: 'var(--space-2)' }}>
          {disconnectError}
        </p>
      )}
    </div>
  );
}

function ChangeFormHint({ message, onCancel }: { message: string; onCancel: () => void }) {
  return (
    <p
      style={{
        color: 'var(--muted)',
        marginTop: 'var(--space-3)',
        fontFamily: 'var(--font-mono)',
        fontSize: 'var(--fs-mono-sm)',
        letterSpacing: 'var(--tracking-mono)',
        textTransform: 'uppercase',
      }}
    >
      ✓ Currently connected. {message}{' '}
      <button
        type="button"
        onClick={onCancel}
        style={{
          background: 'transparent',
          border: 'none',
          padding: 0,
          color: 'var(--ink)',
          fontFamily: 'inherit',
          fontSize: 'inherit',
          letterSpacing: 'inherit',
          textTransform: 'inherit',
          textDecoration: 'underline',
          cursor: 'pointer',
        }}
      >
        cancel
      </button>
    </p>
  );
}

function CardHeader({ title, connected }: { title: string; connected: boolean }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <h2 className="h3" style={{ color: 'var(--ink)' }}>
        {title}
      </h2>
      <span
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 'var(--fs-mono-sm)',
          letterSpacing: 'var(--tracking-mono)',
          textTransform: 'uppercase',
          color: connected ? 'var(--green)' : 'var(--muted)',
        }}
      >
        {connected ? '✓ connected' : 'awaiting'}
      </span>
    </div>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactElement<{ id?: string }>;
}) {
  // useId generates a stable per-instance id; clone the child input to wire
  // htmlFor↔id so screen readers + biome's a11y/noLabelWithoutControl rule
  // both stay happy. Same shape as apps/admin OnboardScreen Field.
  const id = useId();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
      <label
        htmlFor={id}
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 'var(--fs-mono-sm)',
          letterSpacing: 'var(--tracking-mono)',
          textTransform: 'uppercase',
          color: 'var(--muted)',
        }}
      >
        {label}
        {hint && (
          <span style={{ color: 'var(--muted)', textTransform: 'none', marginLeft: 8 }}>
            ({hint})
          </span>
        )}
      </label>
      {cloneElement(children, { id })}
    </div>
  );
}

function FullScreenStatus({ title, body }: { title: string; body?: string }) {
  return (
    <div className="app">
      <header className="app-header">
        <Wordmark />
      </header>
      <main className="center-stack">
        <div className="card" style={{ padding: 'var(--space-6)', maxWidth: 480 }}>
          <h1 className="h3" style={{ color: 'var(--ink)' }}>
            {title}
          </h1>
          {body && <p style={{ color: 'var(--muted)', marginTop: 'var(--space-3)' }}>{body}</p>}
        </div>
      </main>
      <footer className="app-footer">acel·cortex setup</footer>
    </div>
  );
}
